import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { MetaTags } from 'libs/metaTags';
import { createPage } from 'libs/ssr/createPage';
import { useResource } from 'libs/reatom-toolkit';
import { PublicPageTemplate } from 'ui/templates';
import DescriptionCard from 'ui/_components/DescriptionCard/DescriptionCard';
import Button from 'ui/_components/Button/Button';
import descrData from 'ui/staticSiteData/descriptionForPetOwnersData/data';
import { SearchPageTemplate } from 'ui/templates/search-page-template/search-page-template';
import { adaptive } from 'ui/styles/addaptive-provider';
import { useCurrentProfile } from 'features/profile';
import { useServiceTypes } from 'services/service-types';

import s from './description-for-pet-owners.page.module.css';
import { usePetOwnerDescription } from './description-for-pet-owners.page.model';
import bannerImg from './header-bg.jpg';
import { BasicSearchResource } from './pawzr-for-activities.model';

function DescriptionForPetOwnersPage() {
  const {
    isLoading,
    getPetOwnerDescription,
    petOwnerDescription,
  } = usePetOwnerDescription();
  const { isPetOwner } = useCurrentProfile();

  useEffect(() => {
    getPetOwnerDescription();
  }, []);

  if (isPetOwner) {
    const { get, isLoading, data } = useResource(BasicSearchResource);
    const services = useServiceTypes().filter(service => service.is_activities);

    return (
      <React.Fragment>
        <MetaTags {...metaTags} />
        <SearchPageTemplate
          headerImage={bannerImg}
          renderTitle={
            <HeaderBlock>
              <Title>Pawzr for Activities</Title>
              <Description>Search Here For Your Pet Care Needs.</Description>
            </HeaderBlock>
          }
          get={get}
          isLoading={isLoading}
          data={data}
          services={services}
        />
      </React.Fragment>
    );
  } else if (!petOwnerDescription || isLoading) return null;
  else
    return (
      <React.Fragment>
        <MetaTags {...metaTags} />
        <PublicPageTemplate>
          <div className={s.pageWrapper}>
            <section className={s.headerBlock}>
              <div className={s.blockContainer}>
                <h1 className={s.title}>
                  Register as a Pet Owner with Pawzr Today
                </h1>
                <div className={s.helpTitle}>
                  Gain access to our full suite of services. In less time
                  <br />
                  than it takes to walk your dog around the block.
                </div>
                <Link to={'/sign-up-pet-owner'}>
                  <Button className={s.button} isOpacity size="big">
                    Create a profile
                  </Button>
                </Link>
              </div>
            </section>
            <section className={s.blogGrid}>
              {petOwnerDescription.map((item, i) => (
                <DescriptionCard
                  smallDescr={i > 1 && i < 4}
                  key={i}
                  description={item}
                />
              ))}
            </section>
            <div className={s.buttonRow}>
              <Link to={'/sign-up-pet-owner'}>
                <Button className={s.button} size="big">
                  {descrData.en.button.text}
                </Button>
              </Link>
            </div>
          </div>
        </PublicPageTemplate>
      </React.Fragment>
    );
}

const metaTags = {
  title: 'Pawzr For Activities | Pawzr',
};

export default createPage(DescriptionForPetOwnersPage, {
  renderMetaTags: (url, _, host) => <MetaTags {...metaTags} url={url} />,
});

const Title = styled.div`
  font-size: 48px;
  line-height: 56px;
  font-weight: bold;
  color: var(--white);
  ${adaptive} {
    font-size: 24px;
    line-height: 32px;
  }
`;

const Description = styled.div`
  font-size: 18px;
  line-height: 24px;
  font-weight: 500;
  color: var(--white);
  ${adaptive} {
    font-size: 14px;
    line-height: 21px;
  }
`;

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1160px;
  height: 100%;
  margin: 0 auto;
  padding: 0 16px;
  overflow: hidden;
  justify-content: center;
  ${Title} {
    margin-bottom: 8px;
    ${adaptive} {
      margin-bottom: 4px;
    }
  }
`;
